import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../utils/useAuth';
import { getDisplayUserName } from '../utils/userDisplay';
import { editLocksInternals, lockKey, type EditLock, type RemoteLock } from '../utils/realtime/editLocks';

interface UseEditLocksOptions {
  schema: string;
  tableName: string;
  /**
   * Called whenever another session's locks change. Deliberately not React state: the grid
   * repaints cells imperatively and must not re-render on every lock change. Components
   * that do need a render should pass a callback that bumps their own state.
   */
  onChange?: () => void;
  /** Skip the subscription entirely (e.g. before the table name is known). */
  enabled?: boolean;
}

export interface EditLocksApi {
  /** Display name of the other user editing this cell/record, or null if it is free. */
  getLockOwner: (rowId: string | number, colId?: string | null) => string | null;
  /**
   * The other session's lock itself. Two clients can claim the same record inside one
   * presence sync round-trip; comparing sessionId against TAB_SESSION_ID gives both sides
   * the same answer about who yields.
   */
  getLockHolder: (rowId: string | number, colId?: string | null) => RemoteLock | null;
  /**
   * Which of these rows someone else is currently editing. For bulk actions, which write
   * many rows at once and can't do a per-row compare-and-set: skip the locked ones and
   * tell the user whose work was left alone.
   */
  getLockedRows: (rowIds: Array<string | number>) => Array<{ rowId: string; userName: string }>;
  /** Claim a cell (colId given) or a whole record (colId omitted). */
  acquire: (rowId: string | number, colId?: string | null) => void;
  release: (rowId: string | number, colId?: string | null) => void;
  /** Drop every lock this component holds — used on unmount and on save/cancel. */
  releaseAll: () => void;
}

/**
 * Presence-backed edit locks for one table. See utils/realtime/editLocks.ts for why this
 * is presence and not broadcast.
 *
 * The returned functions are stable, so callers can put them in dependency arrays without
 * rebuilding memoised grid config.
 */
export const useEditLocks = ({
  schema,
  tableName,
  onChange,
  enabled = true,
}: UseEditLocksOptions): EditLocksApi => {
  const { appUser, user } = useAuth();
  const userName = getDisplayUserName(appUser, user);

  const regRef = useRef<ReturnType<typeof editLocksInternals.acquireRegistration> | null>(null);
  // Only the keys this component claimed, so unmounting one consumer never drops another's.
  const ownedKeysRef = useRef<Set<string>>(new Set());
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const active = enabled && Boolean(schema) && Boolean(tableName);

  useEffect(() => {
    if (!active) return undefined;

    const reg = editLocksInternals.acquireRegistration(schema, tableName, userName);
    regRef.current = reg;

    const listener = () => onChangeRef.current?.();
    reg.listeners.add(listener);

    const ownedKeys = ownedKeysRef.current;

    return () => {
      reg.listeners.delete(listener);
      if (ownedKeys.size > 0) {
        ownedKeys.forEach((key) => reg.myLocks.delete(key));
        ownedKeys.clear();
        editLocksInternals.publish(reg);
      }
      regRef.current = null;
      editLocksInternals.releaseRegistration(schema, tableName);
    };
  }, [active, schema, tableName, userName]);

  const getLockHolder = useCallback((rowId: string | number, colId?: string | null): RemoteLock | null => {
    const reg = regRef.current;
    if (!reg) return null;
    return editLocksInternals.findLock(reg, rowId, colId);
  }, []);

  const getLockOwner = useCallback((rowId: string | number, colId?: string | null): string | null =>
    getLockHolder(rowId, colId)?.userName ?? null
  , [getLockHolder]);

  const getLockedRows = useCallback((
    rowIds: Array<string | number>
  ): Array<{ rowId: string; userName: string }> => {
    const reg = regRef.current;
    if (!reg) return [];
    const locked: Array<{ rowId: string; userName: string }> = [];
    rowIds.forEach((rowId) => {
      const holder = editLocksInternals.findLock(reg, rowId);
      if (holder) locked.push({ rowId: String(rowId), userName: holder.userName });
    });
    return locked;
  }, []);

  const acquire = useCallback((rowId: string | number, colId?: string | null): void => {
    const reg = regRef.current;
    if (!reg) return;
    const key = lockKey(rowId, colId);
    if (reg.myLocks.has(key)) return;
    const lock: EditLock = { rowId: String(rowId), colId: colId ?? null };
    reg.myLocks.set(key, lock);
    ownedKeysRef.current.add(key);
    editLocksInternals.publish(reg);
  }, []);

  const release = useCallback((rowId: string | number, colId?: string | null): void => {
    const reg = regRef.current;
    if (!reg) return;
    const key = lockKey(rowId, colId);
    if (!reg.myLocks.delete(key)) return;
    ownedKeysRef.current.delete(key);
    editLocksInternals.publish(reg);
  }, []);

  const releaseAll = useCallback((): void => {
    const reg = regRef.current;
    if (!reg || ownedKeysRef.current.size === 0) return;
    ownedKeysRef.current.forEach((key) => reg.myLocks.delete(key));
    ownedKeysRef.current.clear();
    editLocksInternals.publish(reg);
  }, []);

  return { getLockOwner, getLockHolder, getLockedRows, acquire, release, releaseAll };
};
